import { format, parseISO } from 'date-fns';

import { defineInternalTask } from '../../../config/types';
import { internalTasks, reviewTeams } from '../../../config/constants';

/**
 * Internal-only "Inspection Pending" task.
 *
 * Created externally when a vendor home inspection is ordered. The AI review
 * completes the task, but only auto-approves it when the inspection is clean.
 *
 * A multi-phase task (`hasPendingPhase`):
 *  - ordered, vendor not done → no `completedDate`, no `approvalDate` → `pending`
 *  - AI reviewed (clean)      → AI sets `completedDate` + `approvalDate` → `approved` (auto-approved)
 *  - AI reviewed (flagged)    → AI sets `completedDate` only → `needsReview`, awaiting manual approval
 */
export const inspectionPending = defineInternalTask({
  taskName: internalTasks.InspectionPending,
  config: {
    staffDisplayName: 'Inspection Pending',
    reviewTeam: reviewTeams.uwOps,
    policyTypes: ['H'],
    enabled: true,
    hasPendingPhase: true,
    // Never created at bind — created externally on inspection order.
    trigger: () => null,
    description: ({ completedDate, approvalDate, data }) => {
      if (!completedDate) {
        const orderedOn = data.dateOrdered
          ? format(parseISO(data.dateOrdered), 'MM/dd/yyyy @ h:mm aa')
          : 'an unknown date';
        return `An inspection was ordered for this home on ${orderedOn} and has not yet been completed by the vendor.`;
      }
      if (!approvalDate) {
        return 'This inspection has been flagged for follow-up.';
      }
      return 'This inspection has been auto-approved.';
    }
  }
});
